<template>
  <b-card>
    <div class="d-flex flex-wrap align-items-end mb-1">
      <b-form-group label="Search" class="mr-1 mb-1">
        <b-form-input v-model="filters.q" placeholder="URL or body" @keyup.enter="load(1)" />
      </b-form-group>
      <b-form-group label="Method" class="mr-1 mb-1">
        <b-form-select v-model="filters.method" :options="methods" />
      </b-form-group>
      <b-form-group label="Source" class="mr-1 mb-1">
        <b-form-select v-model="filters.scope" :options="scopes" />
      </b-form-group>
      <b-button variant="primary" class="mb-1" :disabled="loading" @click="load(1)">
        {{ loading ? 'Loading…' : 'Search' }}
      </b-button>
    </div>

    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <div class="table-responsive">
      <table class="table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Method</th>
            <th>Status</th>
            <th>URL</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="log in logs">
            <tr :key="log.id" class="cursor-pointer" @click="toggle(log)">
              <td>{{ log.created_at }}</td>
              <td><b-badge :variant="methodVariant(log.method)">{{ log.method }}</b-badge></td>
              <td><b-badge :variant="statusVariant(log.status)">{{ log.status }}</b-badge></td>
              <td class="text-break">{{ log.url }}</td>
            </tr>
            <tr v-if="openId === log.id" :key="`${log.id}-detail`">
              <td colspan="4">
                <p v-if="detailLoading" class="mb-0">Loading request and response…</p>
                <div v-else-if="detail">
                  <h6>Request</h6>
                  <pre class="log-block">{{ detail.request_body || '—' }}</pre>
                  <h6>Response</h6>
                  <pre class="log-block">{{ detail.response_body || '—' }}</pre>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="!loading && logs.length === 0">
            <td colspan="4" class="text-muted">No request logs yet.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="pagination.last_page > 1" class="d-flex justify-content-between align-items-center">
      <span class="text-muted">{{ pagination.total }} logs</span>
      <div>
        <b-button size="sm" class="mr-50" :disabled="pagination.current_page <= 1" @click="load(pagination.current_page - 1)">
          Previous
        </b-button>
        <b-button size="sm" :disabled="pagination.current_page >= pagination.last_page" @click="load(pagination.current_page + 1)">
          Next
        </b-button>
      </div>
    </div>
  </b-card>
</template>

<script>
import {
  BAlert, BBadge, BButton, BCard, BFormGroup, BFormInput, BFormSelect,
} from 'bootstrap-vue'
import { adminPanelApi } from '@/services/adminPanelApi'

export default {
  components: {
    BAlert, BBadge, BButton, BCard, BFormGroup, BFormInput, BFormSelect,
  },
  data() {
    return {
      filters: { q: '', method: '', scope: 'mobile' },
      methods: [
        { value: '', text: 'Any' },
        { value: 'GET', text: 'GET' },
        { value: 'POST', text: 'POST' },
        { value: 'PUT', text: 'PUT' },
        { value: 'PATCH', text: 'PATCH' },
        { value: 'DELETE', text: 'DELETE' },
      ],
      scopes: [
        { value: 'mobile', text: 'Mobile app' },
        { value: 'all', text: 'All API traffic' },
      ],
      logs: [],
      pagination: { current_page: 1, last_page: 1, total: 0 },
      loading: false,
      error: '',
      openId: null,
      detail: null,
      detailLoading: false,
    }
  },
  created() {
    this.load(1)
  },
  methods: {
    async load(page) {
      this.loading = true
      this.error = ''
      this.openId = null
      this.detail = null
      try {
        const res = await adminPanelApi.setupRequestLogs({
          page,
          per_page: 20,
          q: this.filters.q || undefined,
          method: this.filters.method || undefined,
          scope: this.filters.scope,
        })
        this.logs = res.data?.data?.logs || []
        this.pagination = res.data?.pagination || this.pagination
      } catch (error) {
        this.error = error?.response?.data?.message || 'Could not load request logs.'
      } finally {
        this.loading = false
      }
    },
    async toggle(log) {
      if (this.openId === log.id) {
        this.openId = null
        this.detail = null
        return
      }
      this.openId = log.id
      this.detail = null
      this.detailLoading = true
      try {
        const res = await adminPanelApi.setupRequestLog(log.id)
        this.detail = res.data?.data?.log || null
      } catch (error) {
        this.error = error?.response?.data?.message || 'Could not load this log.'
      } finally {
        this.detailLoading = false
      }
    },
    methodVariant(method) {
      const map = { GET: 'info', POST: 'success', PUT: 'warning', PATCH: 'warning', DELETE: 'danger' }
      return map[String(method || '').toUpperCase()] || 'secondary'
    },
    statusVariant(status) {
      const code = String(status || '')
      if (code.startsWith('2')) return 'success'
      if (code.startsWith('4')) return 'warning'
      if (code.startsWith('5')) return 'danger'
      return 'secondary'
    },
  },
}
</script>

<style scoped>
.cursor-pointer { cursor: pointer; }
.log-block {
  white-space: pre-wrap;
  word-break: break-word;
  background: #f8f8f8;
  border-radius: 6px;
  padding: 12px;
  max-height: 280px;
  overflow: auto;
}
</style>
